import { PORTFOLIO_BOOK_KINDS_V2, PORTFOLIO_V2_SCHEMA_VERSION } from "./primitives.js";
import {
  STATEMENT_IMPORT_ERROR_CODES_V2, STATEMENT_IMPORT_EXTRACTORS_V2, STATEMENT_IMPORT_FIELD_NAMES_V2,
  STATEMENT_IMPORT_FILE_EXTRACTION_MODES_V2, STATEMENT_IMPORT_FORMATS_V2,
  STATEMENT_IMPORT_RECORD_DISPOSITIONS_V2, STATEMENT_IMPORT_RECORD_KINDS_V2,
  STATEMENT_IMPORT_REVIEW_STATES_V2, STATEMENT_IMPORT_SEMANTIC_ROLES_V2, STATEMENT_IMPORT_STATES_V2,
  STATEMENT_IMPORT_VALUE_STATES_V2, type PortfolioStatementImportV2, type StatementAccountIdentityV2,
  type StatementEventIdentityV2, type StatementEvidenceLocatorV2, type StatementImportConfirmResponseV2,
  type StatementImportConfirmResponseBindingV2, type StatementImportDispositionCountsV2, type StatementImportFileV2,
  type StatementImportIntentResponseV2, type StatementImportIntentResponseBindingV2,
  type StatementImportIntentV2, type StatementImportOperationStatusV2, type StatementImportReadResponseV2,
  type StatementImportReceiptV2, type StatementImportReconciliationV2, type StatementImportRecordV2,
  type StatementImportStatusResponseBindingV2, type StatementImportStatusResponseV2, type StatementImportUploadBindingV2,
  type StatementImportUploadResponseV2, type StatementInstrumentIdentityV2, type StatementPositionObservationV2,
  type StatementReconciliationSideV2, type StatementStagedFieldV2,
} from "./statement-import.js";
import {
  assertStatementImportConfirmBindingV2, assertStatementImportFieldSemanticsV2,
  assertStatementImportIntentBindingV2, assertStatementImportReadSemanticsV2,
  assertStatementImportReceiptSemanticsV2, assertStatementImportRecordSemanticsV2,
  assertStatementImportStatusBindingV2, assertStatementImportUploadBindingV2,
  parseCanonicalNonnegativeStatementDecimalV2,
} from "./statement-import-response-semantics.js";
export function parseStatementImportUploadResponseV2(value: unknown): StatementImportUploadResponseV2 {
  const source = exact(value, ["ok", "replayed", "import"]);
  if (source.ok !== true || typeof source.replayed !== "boolean") invalid();
  return { ok: true, replayed: source.replayed, import: importRead(source.import) };
}
export function parseStatementImportUploadResponseBoundV2(
  value: unknown, expected: StatementImportUploadBindingV2,
): StatementImportUploadResponseV2 {
  const parsed = parseStatementImportUploadResponseV2(value); const actual = parsed.import;
  assertStatementImportUploadBindingV2(actual, expected);
  return parsed;
}
export function parseStatementImportExtractionResponseV2(value: unknown): StatementImportReadResponseV2 {
  return readResponse(value);
}
export function parseStatementImportReviewResponseV2(value: unknown): StatementImportReadResponseV2 {
  return readResponse(value);
}
export function parseStatementImportReconciliationResponseV2(value: unknown): StatementImportReadResponseV2 {
  return readResponse(value);
}
export function parseStatementImportIntentResponseV2(value: unknown): StatementImportIntentResponseV2 {
  const source = exact(value, ["ok", "replayed", "intent"]);
  if (source.ok !== true || typeof source.replayed !== "boolean") invalid();
  return { ok: true, replayed: source.replayed, intent: intent(source.intent) };
}
export function parseStatementImportIntentResponseBoundV2(
  value: unknown, expected: StatementImportIntentResponseBindingV2,
): StatementImportIntentResponseV2 {
  const parsed = parseStatementImportIntentResponseV2(value); const actual = parsed.intent;
  assertStatementImportIntentBindingV2(actual, expected);
  return parsed;
}
export function parseStatementImportConfirmResponseV2(value: unknown): StatementImportConfirmResponseV2 {
  const source = exact(value, ["ok", "replayed", "receipt"]);
  if (source.ok !== true || typeof source.replayed !== "boolean") invalid();
  return { ok: true, replayed: source.replayed, receipt: receipt(source.receipt) };
}
export function parseStatementImportConfirmResponseBoundV2(
  value: unknown, expected: StatementImportConfirmResponseBindingV2,
): StatementImportConfirmResponseV2 {
  const parsed = parseStatementImportConfirmResponseV2(value); const actual = parsed.receipt;
  assertStatementImportConfirmBindingV2(actual, expected);
  return parsed;
}
export function parseStatementImportStatusResponseV2(value: unknown): StatementImportStatusResponseV2 {
  const source = exact(value, ["ok", "status"]);
  if (source.ok !== true) invalid();
  return { ok: true, status: operationStatus(source.status) };
}
export function parseStatementImportStatusResponseBoundV2(
  value: unknown, expected: StatementImportStatusResponseBindingV2,
): StatementImportStatusResponseV2 {
  const parsed = parseStatementImportStatusResponseV2(value); const actual = parsed.status;
  assertStatementImportStatusBindingV2(actual, expected);
  return parsed;
}
function readResponse(value: unknown): StatementImportReadResponseV2 {
  const source = exact(value, ["ok", "import"]);
  if (source.ok !== true) invalid();
  return { ok: true, import: importRead(source.import) };
}
function importRead(value: unknown): PortfolioStatementImportV2 {
  const s = exact(value, ["schemaVersion", "importId", "bookId", "bookKind", "expectedBookVersion", "reviewRevision", "state",
    "financialAuthority", "uploadOperation", "file", "accountIdentity", "fields", "records", "reconciliation",
    "createdAt", "updatedAt"]);
  if (s.schemaVersion !== PORTFOLIO_V2_SCHEMA_VERSION || s.financialAuthority !== "NONE") invalid();
  const result: PortfolioStatementImportV2 = {
    schemaVersion: PORTFOLIO_V2_SCHEMA_VERSION, importId: id(s.importId), bookId: id(s.bookId),
    bookKind: member(s.bookKind, PORTFOLIO_BOOK_KINDS_V2), expectedBookVersion: uint(s.expectedBookVersion),
    reviewRevision: positiveInt(s.reviewRevision),
    state: member(s.state, STATEMENT_IMPORT_STATES_V2), financialAuthority: "NONE",
    uploadOperation: uploadOperation(s.uploadOperation), file: file(s.file),
    accountIdentity: account(s.accountIdentity), fields: array(s.fields, field, 0, 500),
    records: array(s.records, record, 0, 25_000), reconciliation: reconciliation(s.reconciliation),
    createdAt: iso(s.createdAt), updatedAt: iso(s.updatedAt),
  };
  assertStatementImportReadSemanticsV2(result);
  return result;
}
function uploadOperation(value: unknown) {
  const s = exact(value, ["idempotencyKey", "inputFingerprint"]);
  return { idempotencyKey: id(s.idempotencyKey), inputFingerprint: fingerprint(s.inputFingerprint) };
}
function file(value: unknown): StatementImportFileV2 {
  const s = exact(value, ["fileId", "originalName", "format", "mediaType", "bytes", "sha256", "extractionMode"]);
  const format = member(s.format, STATEMENT_IMPORT_FORMATS_V2);
  const mode = nullableMember(s.extractionMode, STATEMENT_IMPORT_FILE_EXTRACTION_MODES_V2);
  if ((format === "CSV" || format === "OFX" || format === "QFX") && mode !== null && mode !== "STRUCTURED") invalid();
  if (format === "PDF" && mode === "STRUCTURED") invalid();
  if (format === "IMAGE" && mode !== null && mode !== "OCR") invalid();
  return { fileId: id(s.fileId), originalName: text(s.originalName, 1, 255), format,
    mediaType: text(s.mediaType, 1, 120), bytes: positiveInt(s.bytes), sha256: rawSha(s.sha256), extractionMode: mode };
}
function evidence(value: unknown): StatementEvidenceLocatorV2 {
  const s = exact(value, ["fileId", "row", "page", "bbox", "textDigest"]);
  const row = nullablePositiveInt(s.row); const page = nullablePositiveInt(s.page);
  if ((row === null) === (page === null)) invalid();
  let bbox: StatementEvidenceLocatorV2["bbox"] = null;
  if (s.bbox !== null) {
    const b = exact(s.bbox, ["x", "y", "width", "height", "unit"]);
    bbox = { x: finiteNonnegative(b.x), y: finiteNonnegative(b.y), width: finitePositive(b.width),
      height: finitePositive(b.height), unit: member(b.unit, ["POINT", "PIXEL"] as const) };
    if (page === null) invalid();
  }
  return { fileId: id(s.fileId), row, page, bbox, textDigest: fingerprint(s.textDigest) };
}
function field(value: unknown): StatementStagedFieldV2 {
  const s = exact(value, ["fieldId", "name", "semanticRole", "valueState", "rawValue", "normalizedValue",
    "extractor", "confidenceBps", "reviewState", "reviewedAt", "evidence"]);
  const result: StatementStagedFieldV2 = {
    fieldId: id(s.fieldId), name: member(s.name, STATEMENT_IMPORT_FIELD_NAMES_V2),
    semanticRole: member(s.semanticRole, STATEMENT_IMPORT_SEMANTIC_ROLES_V2),
    valueState: member(s.valueState, STATEMENT_IMPORT_VALUE_STATES_V2), rawValue: nullableText(s.rawValue, 10_000),
    normalizedValue: nullableText(s.normalizedValue, 2_000), extractor: member(s.extractor, STATEMENT_IMPORT_EXTRACTORS_V2),
    confidenceBps: nullableRangeInt(s.confidenceBps, 0, 10_000), reviewState: member(s.reviewState, STATEMENT_IMPORT_REVIEW_STATES_V2),
    reviewedAt: nullableIso(s.reviewedAt), evidence: array(s.evidence, evidence, 0, 20),
  };
  assertStatementImportFieldSemanticsV2(result);
  return result;
}
function account(value: unknown): StatementAccountIdentityV2 {
  const s = exact(value, ["state", "providerName", "maskedAccountRef", "currency", "sourceTimezone", "statementId",
    "periodStart", "periodEnd", "asOf"]);
  const result: StatementAccountIdentityV2 = { state: member(s.state, ["RESOLVED", "UNKNOWN", "CONFLICT"] as const),
    providerName: nullableText(s.providerName, 80), maskedAccountRef: nullableText(s.maskedAccountRef, 80),
    currency: nullableCurrency(s.currency), sourceTimezone: nullableText(s.sourceTimezone, 80),
    statementId: nullableText(s.statementId, 160), periodStart: nullableIso(s.periodStart),
    periodEnd: nullableIso(s.periodEnd), asOf: nullableIso(s.asOf) };
  if ((result.periodStart === null) !== (result.periodEnd === null)) invalid();
  if (result.periodStart && result.periodEnd && Date.parse(result.periodStart) > Date.parse(result.periodEnd)) invalid();
  if (result.state === "RESOLVED" && (!result.providerName || !result.maskedAccountRef || !result.currency
    || !result.sourceTimezone || (!result.asOf && !result.periodEnd))) invalid();
  return result;
}
function instrument(value: unknown): StatementInstrumentIdentityV2 {
  const s = exact(value, ["state", "brokerInstrumentId", "isin", "cusip", "figi", "symbol", "exchange", "currency",
    "resolvedInstrumentId"]);
  const result: StatementInstrumentIdentityV2 = { state: member(s.state, ["RESOLVED", "UNKNOWN", "AMBIGUOUS"] as const),
    brokerInstrumentId: nullableText(s.brokerInstrumentId, 160), isin: nullableText(s.isin, 32),
    cusip: nullableText(s.cusip, 32), figi: nullableText(s.figi, 32), symbol: nullableText(s.symbol, 40),
    exchange: nullableText(s.exchange, 80), currency: nullableCurrency(s.currency),
    resolvedInstrumentId: nullableText(s.resolvedInstrumentId, 160) };
  const exactIdentity = Boolean(result.brokerInstrumentId || result.isin || result.cusip || result.figi);
  const marketIdentity = Boolean(result.symbol && result.exchange && result.currency);
  if (result.state === "RESOLVED" && (!result.resolvedInstrumentId || (!exactIdentity && !marketIdentity))) invalid();
  if (result.state !== "RESOLVED" && result.resolvedInstrumentId !== null) invalid();
  return result;
}
function eventIdentity(value: unknown): StatementEventIdentityV2 {
  const s = exact(value, ["strength", "externalEventId", "orderId", "executionId", "canonicalFingerprint"]);
  const result: StatementEventIdentityV2 = { strength: member(s.strength, ["STRONG", "WEAK", "MISSING", "CONFLICT"] as const),
    externalEventId: nullableText(s.externalEventId, 160), orderId: nullableText(s.orderId, 160),
    executionId: nullableText(s.executionId, 160), canonicalFingerprint: nullableFingerprint(s.canonicalFingerprint) };
  const external = Boolean(result.externalEventId || result.orderId || result.executionId);
  if (result.strength === "STRONG" && (!external || !result.canonicalFingerprint)) invalid();
  if (result.strength === "WEAK" && !result.canonicalFingerprint) invalid();
  if (result.strength === "MISSING" && (external || result.canonicalFingerprint)) invalid();
  return result;
}
function record(value: unknown): StatementImportRecordV2 {
  const s = exact(value, ["recordId", "kind", "semanticRole", "disposition", "fields", "instrumentIdentity",
    "eventIdentity", "existingTargetIds", "issueCodes"]);
  const result: StatementImportRecordV2 = { recordId: id(s.recordId), kind: member(s.kind, STATEMENT_IMPORT_RECORD_KINDS_V2),
    semanticRole: member(s.semanticRole, STATEMENT_IMPORT_SEMANTIC_ROLES_V2),
    disposition: member(s.disposition, STATEMENT_IMPORT_RECORD_DISPOSITIONS_V2), fields: array(s.fields, field, 1, 100),
    instrumentIdentity: s.instrumentIdentity === null ? null : instrument(s.instrumentIdentity),
    eventIdentity: s.eventIdentity === null ? null : eventIdentity(s.eventIdentity),
    existingTargetIds: unique(array(s.existingTargetIds, id, 0, 100)),
    issueCodes: unique(array(s.issueCodes, (item) => member(item, STATEMENT_IMPORT_ERROR_CODES_V2), 0, 100)) };
  assertStatementImportRecordSemanticsV2(result);
  return result;
}
function position(value: unknown): StatementPositionObservationV2 {
  const s = exact(value, ["instrumentId", "quantity", "evidenceIds"]);
  return { instrumentId: id(s.instrumentId), quantity: parseCanonicalNonnegativeStatementDecimalV2(s.quantity),
    evidenceIds: unique(array(s.evidenceIds, id, 1, 100)) };
}
function side(value: unknown): StatementReconciliationSideV2 {
  const s = exact(value, ["cashBalance", "positions"]);
  const positions = array(s.positions, position, 0, 10_000);
  if (new Set(positions.map((item) => item.instrumentId)).size !== positions.length) invalid();
  return { cashBalance: s.cashBalance === null ? null : parseCanonicalNonnegativeStatementDecimalV2(s.cashBalance), positions };
}
function counts(value: unknown): StatementImportDispositionCountsV2 {
  const s = exact(value, [...STATEMENT_IMPORT_RECORD_DISPOSITIONS_V2]);
  return { NEW: uint(s.NEW), BIND_EXISTING: uint(s.BIND_EXISTING), SNAPSHOT_ONLY: uint(s.SNAPSHOT_ONLY),
    POSSIBLE_DUPLICATE: uint(s.POSSIBLE_DUPLICATE), CONFLICT: uint(s.CONFLICT), IGNORE: uint(s.IGNORE) };
}
function reconciliation(value: unknown): StatementImportReconciliationV2 {
  const s = exact(value, ["state", "inputFingerprint", "current", "statement", "proposedObservation",
    "dispositionCounts", "blockingCodes", "calculatedAt"]);
  const result: StatementImportReconciliationV2 = { state: member(s.state, ["NOT_RUN", "BLOCKED", "MATCHED", "CONFLICTED"] as const),
    inputFingerprint: nullableFingerprint(s.inputFingerprint),
    current: s.current === null ? null : side(s.current), statement: s.statement === null ? null : side(s.statement),
    proposedObservation: s.proposedObservation === null ? null : side(s.proposedObservation), dispositionCounts: counts(s.dispositionCounts),
    blockingCodes: unique(array(s.blockingCodes, (item) => member(item, STATEMENT_IMPORT_ERROR_CODES_V2), 0, 100)),
    calculatedAt: nullableIso(s.calculatedAt) };
  if (result.state === "MATCHED" && (result.blockingCodes.length || !result.calculatedAt || !result.statement)) invalid();
  if ((result.state === "BLOCKED" || result.state === "CONFLICTED") && result.blockingCodes.length === 0) invalid();
  if ((result.state === "NOT_RUN") !== (result.inputFingerprint === null)) invalid();
  return result;
}
function intent(value: unknown): StatementImportIntentV2 {
  const s = exact(value, ["intentId", "importId", "exactBookId", "challengeToken", "expiresAt", "inputFingerprint",
    "dispositionCounts", "financialAuthority", "observationsOnlyAcknowledgementRequired"]);
  if (s.financialAuthority !== "NONE" || s.observationsOnlyAcknowledgementRequired !== true) invalid();
  return { intentId: id(s.intentId), importId: id(s.importId), exactBookId: id(s.exactBookId),
    challengeToken: text(s.challengeToken, 8, 160), expiresAt: iso(s.expiresAt), inputFingerprint: fingerprint(s.inputFingerprint),
    dispositionCounts: counts(s.dispositionCounts), financialAuthority: "NONE", observationsOnlyAcknowledgementRequired: true };
}
function receipt(value: unknown): StatementImportReceiptV2 {
  const s = exact(value, ["receiptId", "importId", "bookId", "state", "inputFingerprint", "dispositionCounts",
    "acceptedObservationIds", "financialAuthority", "financialWrites", "committedAt"]);
  if (s.financialAuthority !== "NONE") invalid();
  const writes = exact(s.financialWrites, ["ledgerTransactions", "lots", "executions", "cashEvents"]);
  if (writes.ledgerTransactions !== 0 || writes.lots !== 0 || writes.executions !== 0 || writes.cashEvents !== 0) invalid();
  const result: StatementImportReceiptV2 = { receiptId: id(s.receiptId), importId: id(s.importId), bookId: id(s.bookId),
    state: member(s.state, ["OBSERVATIONS_ACCEPTED", "DUPLICATE_NOOP"] as const), inputFingerprint: fingerprint(s.inputFingerprint),
    dispositionCounts: counts(s.dispositionCounts),
    acceptedObservationIds: unique(array(s.acceptedObservationIds, id, 0, 25_000)), financialAuthority: "NONE",
    financialWrites: { ledgerTransactions: 0, lots: 0, executions: 0, cashEvents: 0 }, committedAt: iso(s.committedAt) };
  assertStatementImportReceiptSemanticsV2(result);
  return result;
}
function operationStatus(value: unknown): StatementImportOperationStatusV2 {
  const s = exact(value, ["operation", "state", "importId", "bookId", "idempotencyKey", "inputFingerprint", "receipt"]);
  const result: StatementImportOperationStatusV2 = { operation: member(s.operation,
    ["UPLOAD", "EXTRACTION", "REVIEW", "RECONCILIATION", "ACCEPT_INTENT", "ACCEPT_CONFIRM", "DISCARD"] as const),
    state: member(s.state, ["NOT_FOUND", "PENDING", "COMMITTED", "CONFLICT", "ABANDONED", "OUTCOME_UNKNOWN"] as const),
    importId: s.importId === null ? null : id(s.importId), bookId: id(s.bookId), idempotencyKey: id(s.idempotencyKey),
    inputFingerprint: fingerprint(s.inputFingerprint),
    receipt: s.receipt === null ? null : receipt(s.receipt) };
  const receiptRequired = result.operation === "ACCEPT_CONFIRM" && result.state === "COMMITTED";
  if (receiptRequired !== (result.receipt !== null) || (result.receipt && result.state !== "COMMITTED")) invalid();
  if (result.state === "NOT_FOUND" && result.importId !== null) invalid();
  if (result.importId === null && result.state !== "NOT_FOUND" && result.state !== "OUTCOME_UNKNOWN") invalid();
  if (result.receipt && (result.receipt.inputFingerprint !== result.inputFingerprint
    || result.receipt.bookId !== result.bookId || result.receipt.importId !== result.importId)) invalid();
  return result;
}
function exact(value: unknown, keys: readonly string[]): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) invalid();
  const result = value as Record<string, unknown>; const expected = new Set(keys);
  if (Object.keys(result).length !== expected.size || Object.keys(result).some((key) => !expected.has(key))) invalid();
  return result;
}
function array<T>(value: unknown, parse: (item: unknown) => T, minimum: number, maximum: number): T[] {
  if (!Array.isArray(value) || value.length < minimum || value.length > maximum) invalid();
  return value.map(parse);
}
function unique<T extends string>(value: T[]): T[] { if (new Set(value).size !== value.length) invalid(); return value; }
function member<const T extends readonly string[]>(value: unknown, values: T): T[number] {
  if (typeof value !== "string" || !values.includes(value)) invalid(); return value as T[number];
}
function nullableMember<const T extends readonly string[]>(value: unknown, values: T): T[number] | null {
  return value === null ? null : member(value, values); }
function text(value: unknown, minimum: number, maximum: number): string {
  if (typeof value !== "string" || value.trim() !== value || value.length < minimum || value.length > maximum) invalid(); return value;
}
function nullableText(value: unknown, maximum: number): string | null { return value === null ? null : text(value, 1, maximum); }
function id(value: unknown): string { const result = text(value, 3, 160); if (!/^[A-Za-z0-9._:/-]+$/u.test(result)) invalid(); return result; }
function uint(value: unknown): number { if (!Number.isSafeInteger(value) || Number(value) < 0) invalid(); return Number(value); }
function positiveInt(value: unknown): number { const result = uint(value); if (result === 0) invalid(); return result; }
function nullablePositiveInt(value: unknown): number | null { return value === null ? null : positiveInt(value); }
function nullableRangeInt(value: unknown, min: number, max: number): number | null {
  if (value === null) return null; const result = uint(value); if (result < min || result > max) invalid(); return result; }
function finiteNonnegative(value: unknown): number { if (typeof value !== "number" || !Number.isFinite(value) || value < 0) invalid(); return value; }
function finitePositive(value: unknown): number { const result = finiteNonnegative(value); if (result === 0) invalid(); return result; }
function iso(value: unknown): string { const result = text(value, 20, 40); if (!Number.isFinite(Date.parse(result))) invalid(); return result; }
function nullableIso(value: unknown): string | null { return value === null ? null : iso(value); }
function fingerprint(value: unknown): string { const result = text(value, 74, 74); if (!/^sha256:v1:[0-9a-f]{64}$/u.test(result)) invalid(); return result; }
function nullableFingerprint(value: unknown): string | null { return value === null ? null : fingerprint(value); }
function rawSha(value: unknown): string { const result = text(value, 64, 64); if (!/^[0-9a-f]{64}$/u.test(result)) invalid(); return result; }
function nullableCurrency(value: unknown): string | null { if (value === null) return null; const result = text(value, 3, 6); if (!/^[A-Z]{3,6}$/u.test(result)) invalid(); return result; }
function invalid(): never { throw new Error("STATEMENT_IMPORT_RESPONSE_INVALID"); }
